import {ElementRef, Injectable} from '@angular/core';
import {Box3, Euler, Matrix4, Object3D, Quaternion, Vector3, WebGLRenderer} from 'three';
import {CameraService} from './camera.service';
import {SceneService} from './scene.service';
import {ContainerService} from '../../Utils/container.service';


@Injectable({
    providedIn: 'root'
})
export class RenderService {

    public renderer: WebGLRenderer;
    public renderer_colorCoding: WebGLRenderer;

    private _canvas: HTMLCanvasElement;
    private _cameraUpChanged = false;

    constructor(private cameraService: CameraService,
                private sceneService: SceneService,
                private containerService: ContainerService) {
    }

    set canvas(canvas: HTMLCanvasElement) {
        this._canvas = canvas;
    }

    get canvas(): HTMLCanvasElement {
        return this._canvas;
    }

    public initRenderer(canvas, relaod = false) {
        this._canvas = canvas;
        // if (this.renderer === undefined || this.renderer === null || relaod) {
        //     this.containerService.canvas = this._canvas;
        // }
        this.renderer = new WebGLRenderer({
            canvas: this._canvas,
            antialias: true,
            alpha: false,
            preserveDrawingBuffer: true
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this._canvas.width, this._canvas.height, false);
        this.renderer.render(this.sceneService.scene, this.cameraService.camera);
        this.renderer_colorCoding = new WebGLRenderer({
            antialias: true,
            alpha: false,
            preserveDrawingBuffer: true
        });
        // this.renderer_colorCoding.setPixelRatio(canvas.width / canvas.modelHeight);
        // this.renderer_colorCoding.setSize(canvas.width, canvas.modelHeight);
    }

    public render() {
        // TODO: mwimmer only if picture is superimpose

        this.cameraService.camera.updateProjectionMatrix();
        this.cameraService.camera.updateMatrix();
        const mesh = this.sceneService.scene.getObjectByName('imagePlane');

        if (mesh && this.cameraService.rotateImage) {
            this.rotateImageWithCamera(mesh);
        }

        this.cameraService.setPreviousRotation();
        this.renderer.render(this.sceneService.scene, this.cameraService.camera);
    }

    private rotateImageWithCamera(mesh) {
        // uncomment to see the effect of the code:

        const camera = this.cameraService.camera;
        const direction = new Vector3();
        camera.getWorldDirection(direction);
        mesh.position.copy(camera.position).add(direction.multiplyScalar(1));

        if (this._cameraUpChanged) {
            // rotate image and camera around different axis
            const difference = camera.rotation.toVector3().sub(this.cameraService.previousRotation);
            const rotation = new Euler().setFromVector3(mesh.rotation.toVector3().add(difference));
            mesh.rotation.setFromVector3(rotation.toVector3());
            mesh.updateMatrix();
        } else if (!this.containerService.imageAligned) {
            // rotate image and camera around same axis
            const rotation = camera.rotation.clone();
            mesh.rotation.setFromVector3(rotation.toVector3());
            mesh.updateMatrix();
        }

        this._cameraUpChanged = this.containerService.imageAligned && !camera.quaternion.equals(this.cameraService.defaultQuaternion);
    }

    /**
     * updates canvas size
     */
    public onResize(width, height) {
        this.renderer.setSize(width, height, false);
        this.renderer_colorCoding.setSize(width, height);
        this.cameraService.camera.aspect = width / height;
        // TODO calculate new camera position
        this.cameraService.canvasWidth = width;
        this.cameraService.canvasHeight = height;
        this.cameraService.camera.updateProjectionMatrix();
        this.setCameraZ(this.containerService.renderGroup.children[0]);
        this.render();
    }

    /**
     * Set the camera z position so that the model fits in the canvas on the screen.
     */
    public setCameraZ(object: Object3D) {
        const offset = 5;

        const boundingBox = new Box3();
        boundingBox.setFromObject(object);

        const center = boundingBox.getCenter(new Vector3());
        const size = boundingBox.getSize(new Vector3());

        const camera = this.cameraService.camera;
        const startDistance = center.distanceTo(camera.position);

        // check if the screen is horizontal or vertical, because camera.fov is
        // based on the vertical direction
        const endDistance = camera.aspect > 1 ?
            ((size.y / 2) + offset) / Math.abs(Math.tan(camera.fov / 2)) :
            ((size.y / 2) + offset) / Math.abs(Math.tan(camera.fov / 2)) / camera.aspect;

        camera.position.set(
            camera.position.x * endDistance / startDistance,
            camera.position.y * endDistance / startDistance,
            camera.position.z * endDistance / startDistance,
        );
        camera.lookAt(center);
        this.render();
    }
}
